import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { UserVM } from '@tto/shared-models';
import { useAuth } from 'src/features/authentication';
import { useGetLoggedInUserQuery } from 'src/services/restApi/queries/users.service';
import { UserList } from './components/UserList';
import { UserDetailDrawer } from './components/UserDetailDrawer';
import { DeactivateUserDialog, ReinstateUserDialog } from './components/ConfirmationDialogs';

export const UserManagement = () => {
  // Current user (from DB)
  const { user: authUser } = useAuth();
  const { data: currentUser } = useGetLoggedInUserQuery(authUser?.uid ?? '', {
    skip: !authUser?.uid,
  });
  const currentUserRole = currentUser?.role ?? 'USER';

  // Drawers state
  const [selectedUser, setSelectedUser] = useState<UserVM | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Confirmation dialogs state
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [reinstateDialogOpen, setReinstateDialogOpen] = useState(false);

  const [userToAction, setUserToAction] = useState<UserVM | null>(null);

  // Handle user click from list
  const handleUserClick = (user: UserVM) => {
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // Don't clear selectedUser immediately to allow for exit animation
    setTimeout(() => setSelectedUser(null), 300);
  };

  // Handle action dialogs
  const handleDeactivate = (user: UserVM) => {
    setUserToAction(user);
    setDeactivateDialogOpen(true);
    setDrawerOpen(false);
  };

  const handleReinstate = (user: UserVM) => {
    setUserToAction(user);
    setReinstateDialogOpen(true);
    setDrawerOpen(false);
  };

  // Handle action success (close dialog and clear state)
  const handleActionSuccess = () => {
    setUserToAction(null);
    setSelectedUser(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant='h5' fontWeight={600} gutterBottom>
          User Management
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Manage user accounts, roles, and access permissions
        </Typography>
      </Box>

      <UserList onUserClick={handleUserClick} />

      {/* User Detail Drawer */}
      <UserDetailDrawer
        user={selectedUser}
        open={drawerOpen}
        onClose={handleDrawerClose}
        currentUserRole={currentUserRole}
        onDeactivate={handleDeactivate}
        onReinstate={handleReinstate}
      />

      {/* Confirmation Dialogs */}
      <DeactivateUserDialog
        user={userToAction}
        open={deactivateDialogOpen}
        onClose={() => {
          setDeactivateDialogOpen(false);
          setUserToAction(null);
        }}
        onSuccess={handleActionSuccess}
      />

      <ReinstateUserDialog
        user={userToAction}
        open={reinstateDialogOpen}
        onClose={() => {
          setReinstateDialogOpen(false);
          setUserToAction(null);
        }}
        onSuccess={handleActionSuccess}
      />
    </Box>
  );
};
